import { Button, TextField, Typography } from "@material-ui/core";
import { Formik } from "formik";
import { get } from "lodash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "../../../../../../components/Dialog";
import {
  freezeReceivables,
  getFactorClient,
  getFreezeActionPending
} from "../../../../../../modules/factoringClient";
import {
  closeDialog,
  getDialog,
  openSnackbar
} from "../../../../../../modules/ui";

export const CONFIRM_FREEZE_DIALOG_VARIANT = "confirm-freeze-receivables";

interface FormValues {
  reason: string;
}

const ConfirmFreezeDialog = (): JSX.Element => {
  const dispatch = useDispatch();
  const dialog = useSelector(getDialog);
  const factoring: any = useSelector(getFactorClient);
  const pending = useSelector(getFreezeActionPending);

  const open = dialog.variant === CONFIRM_FREEZE_DIALOG_VARIANT;
  const factoringId = get(factoring, "id");
  const clientName =
    get(factoring, "company_profile.name") ||
    get(factoring, "user.email") ||
    "this client";

  const handleClose = (): void => {
    dispatch(closeDialog());
  };

  const validate = (values: FormValues): { reason?: string } => {
    if (!values.reason || values.reason.trim().length === 0) {
      return { reason: "Reason is required." };
    }
    return {};
  };

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={handleClose}>
      <Formik<FormValues>
        enableReinitialize
        initialValues={{ reason: "" }}
        validate={validate}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            await dispatch(
              freezeReceivables(factoringId, values.reason.trim())
            );
            dispatch(openSnackbar("success", "Factored receivables frozen."));
            resetForm();
            handleClose();
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          errors,
          touched,
          isValid
        }): JSX.Element => (
          <>
            <DialogTitle onClose={handleClose}>
              Freeze Factored Receivables
            </DialogTitle>
            <DialogContent>
              <Typography paragraph>
                You are about to <strong>freeze all factored receivables</strong>{" "}
                for <strong>{clientName}</strong>. This is a mechanical lock —
                non-dev users will be blocked from:
              </Typography>
              <Typography>
                • Manual cash application on any factored FR
              </Typography>
              <Typography>• Manual purchase creation or editing</Typography>
              <Typography>• Manual obligation edits</Typography>
              <Typography>• Chargeback / give-back entries</Typography>
              <Typography>
                • Background jobs: auto-apply-cash, auto-purchase,
                reserve-recovery
              </Typography>
              <TextField
                name="reason"
                label="Reason / Notes"
                placeholder="e.g. HD Buyout cutover — freeze window"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.reason && errors.reason)}
                helperText={touched.reason && errors.reason}
                multiline
                rowsMax={5}
                fullWidth
                required
                margin="normal"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={pending}>
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={(): void => handleSubmit()}
                disabled={pending || !isValid}
              >
                {pending ? "Freezing..." : "Confirm Freeze"}
              </Button>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default ConfirmFreezeDialog;
